// inv/src/blocks/Homepage/LastDealsInline.jsx
import { useEffect, useState } from 'react'
import { API_URL } from '../../../../config'
import { useTranslation } from 'react-i18next'
import { useInitialData } from '../../../../components/data/DataContext'
import InvestorLogo from '../../../../components/data/InvestorLogo'
import { Link } from 'react-router-dom'
import Timeline from '../../../../components/ui/lastdeals3'
import { useDealCardCollection } from '../../../../hooks/deal-card'
import { useMembershipAccess } from '../../../../hooks/membership'
import { buildInvestorProfilePath } from '../../../../components/investor-display/helpers'

const LastDealsInline = ({ className = '' }) => {
  const { t, i18n } = useTranslation()
  const { latestDeals, investorMap: ssrInvestorMap = {} } = useInitialData()
  const { isProfessional } = useMembershipAccess()

  const ssrDeals =
    Array.isArray(latestDeals) && latestDeals.length > 0 ? latestDeals : null

  const [data, setData] = useState(ssrDeals ?? [])
  const [loading, setLoading] = useState(ssrDeals === null)
  const [error, setError] = useState(null)
  const [investorMap, setInvestorMap] = useState(ssrInvestorMap)

  const buildInvestorChip = (dbId, idx, total) => {
    const inv = investorMap[dbId]
    if (!inv) return dbId
    const to = buildInvestorProfilePath({ investor: inv, language: i18n.language })

    return (
      <Link
        key={dbId}
        to={to}
        className="inline-flex items-center gap-1 hover:underline"
      >
        <InvestorLogo
          id={inv.id}
          country={inv.country}
          className="w-4 h-4"
          alt={`${inv.name} logo`}
        />
        <span>{inv.name}{idx < total - 1 ? '' : ''}</span>
      </Link>
    )
  }

  const { items, surfaceConfig } = useDealCardCollection({
    deals: data,
    surfaceKey: 'earlyStageInline',
    buildInvestorNode: buildInvestorChip,
  })
  const shouldRefetchDeals =
    Boolean(surfaceConfig.refetchOnProfessionalUpgrade) &&
    isProfessional

  // load deals
  useEffect(() => {
    if (ssrDeals !== null && !shouldRefetchDeals) {
      setLoading(false)
      return
    }
    setLoading(true)
    fetch(`${API_URL}/latest-2-seed-deals-public`, { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => setData(res.deals || []))
      .catch((e) => {
        console.error(e)
        setError(e.message)
      })
      .finally(() => setLoading(false))
  }, [shouldRefetchDeals, ssrDeals])

  // load investors
  useEffect(() => {
    if (!data?.length) return

    const ids = [
      ...new Set(
        (data || []).flatMap((d) => [
          ...(d.lead_investors || []),
          ...(d.other_investors_participated || []),
        ])
      ),
    ]

    const missing = ids.filter((id) => !investorMap[id])
    if (!missing.length) return

    Promise.all(
      missing.map((_id) =>
        fetch(`${API_URL}/investors-limited/${_id}?by=_id`)
          .then((res) => (res.ok ? res.json() : Promise.reject(res)))
          .then((inv) => ({
            _id,
            id: inv.id,
            name: inv.name,
            country: inv.country,
          }))
          .catch(() => null)
      )
    ).then((results) => {
      const updated = { ...investorMap }
      results.forEach((p) => {
        if (p) updated[p._id] = { id: p.id, name: p.name, country: p.country }
      })
      setInvestorMap(updated)
    })
  }, [data, investorMap])

  const moreText = t('investorCard.readMore', { defaultValue: 'Read more' })
  const lessText = t('investorCard.showLess', { defaultValue: 'Show less' })

  return (
    <div
      className={`w-full py-4 sm:py-5 ${className}`}
    >
      {loading ? (
        <div className="flex items-center justify-center text-sm opacity-70">
          Loading...
        </div>
      ) : error ? (
        <p className="text-red-500 text-sm">Error: {error}</p>
      ) : items.length > 0 ? (
        <Timeline
          items={items}
          className="max-w-full"
          moreText={moreText}
          lessText={lessText}
          recipientAccess={surfaceConfig.recipientCompany.accessMode}
        />
      ) : (
        <p className="text-sm text-stone-500">
          {t('pageHome.content.noDeals', 'No recent deals to display.')}
        </p>
      )}
    </div>
  )
}

export default LastDealsInline
